/**
 * Formatage — tout en français (fr-FR).
 *
 * Les `Intl.*Format` sont coûteux à instancier : on les crée une fois au
 * chargement du module plutôt qu'à chaque cellule de tableau.
 */

const integerFormatter = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
const decimalFormatter = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });
const compactFormatter = new Intl.NumberFormat('fr-FR', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function formatInteger(value: number): string {
  return integerFormatter.format(value);
}

export function formatDecimal(value: number): string {
  return decimalFormatter.format(value);
}

/**
 * Pourcentage à une décimale, avec l'espace typographique française.
 *
 * U+202F (espace fine insécable) et non une espace ordinaire : en SVG,
 * Recharts coupe les étiquettes sur les espaces, et « 8,6 % » se retrouvait
 * écrit sur deux lignes au-dessus des colonnes. L'espace insécable règle le
 * problème d'affichage et respecte la typographie française, qui veut bien une
 * espace avant le signe pourcent.
 */
export function formatPercent(value: number): string {
  return `${decimalFormatter.format(value)}\u202F%`;
}

/** 1 284 → « 1,3 k ». Réservé aux graduations d'axe, jamais aux valeurs lues. */
export function formatCompact(value: number): string {
  return compactFormatter.format(value);
}

/**
 * Secondes → durée lisible, avec une précision adaptée à l'ordre de grandeur.
 *
 * Trois régimes, parce qu'une même règle ne convient pas à tout :
 *   · sous l'heure, les secondes comptent — une session d'apprentissage dure
 *     2 min 44 s, et l'arrondir à « 3 min » perd 16 s sur 164, soit 10 % ;
 *   · entre 1 h et 100 h, les minutes suffisent ;
 *   · au-delà, on s'en tient aux heures : « 3 245 h 12 min » donne une fausse
 *     impression de précision sur un cumul.
 */
export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '0 s';

  const rounded = Math.round(totalSeconds);
  const hours = Math.floor(rounded / 3600);

  if (hours >= 100) return `${formatInteger(hours)} h`;

  if (hours > 0) {
    const minutes = Math.round((rounded % 3600) / 60);
    return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  }

  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  if (minutes === 0) return `${seconds} s`;
  return seconds > 0 ? `${minutes} min ${seconds} s` : `${minutes} min`;
}

const shortDateFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
  timeZone: 'UTC',
});

const longDateFormatter = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * `2026-09-18` → « 18 sept. ». Pour les graduations d'axe.
 *
 * Interprété en UTC : la chaîne désigne un jour civil, pas un instant. La lire
 * dans le fuseau du navigateur la ferait reculer d'un jour à l'ouest de
 * Greenwich.
 */
export function formatDayShort(isoDate: string): string {
  return shortDateFormatter.format(new Date(`${isoDate}T00:00:00Z`));
}

const monthYearFormatter = new Intl.DateTimeFormat('fr-FR', {
  month: 'short',
  year: '2-digit',
  timeZone: 'UTC',
});

/**
 * `2026-09-01` → « sept. 26 ». Pour les cohortes mensuelles.
 *
 * L'année est indispensable : sur une période de plus d'un an, deux cohortes
 * mensuelles du même mois s'affichaient toutes deux « 01 sept. » et devenaient
 * indiscernables sur l'axe.
 */
export function formatMonthShort(isoDate: string): string {
  return monthYearFormatter.format(new Date(`${isoDate}T00:00:00Z`));
}

/** `2026-09-18` → « vendredi 18 septembre 2026 ». Pour les infobulles. */
export function formatDayLong(isoDate: string): string {
  return longDateFormatter.format(new Date(`${isoDate}T00:00:00Z`));
}

const dateTimeFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Paris',
});

/**
 * Instant ISO → date/heure de Paris.
 *
 * L'API renvoie de l'UTC ; l'utilisateur raisonne en heure de Paris, comme
 * toutes les agrégations.
 */
export function formatInstant(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return dateTimeFormatter.format(date);
}

/**
 * Formateurs monétaires, un par devise et par précision, créés à la demande.
 *
 * La devise vient de l'API (celle de la boutique Shopify) : elle n'est pas
 * figée à l'euro dans le code.
 */
const moneyFormatters = new Map<string, Intl.NumberFormat>();

function moneyFormatter(currency: string, cents: boolean): Intl.NumberFormat {
  const key = `${currency}:${cents ? 2 : 0}`;
  let formatter = moneyFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
      minimumFractionDigits: cents ? 2 : 0,
      maximumFractionDigits: cents ? 2 : 0,
    });
    moneyFormatters.set(key, formatter);
  }
  return formatter;
}

/**
 * Montant → « 12 345 € ».
 *
 * Sans centimes par défaut : sur un chiffre d'affaires, « 12 345,67 € » donne
 * une fausse impression de précision et se lit moins vite. `cents` pour les
 * petits montants où ils comptent — un panier moyen.
 */
export function formatMoney(value: number, currency: string, cents = false): string {
  return moneyFormatter(currency, cents).format(value);
}

/** 12 840 € → « 12,8 k€ ». Réservé aux graduations d'axe. */
export function formatMoneyCompact(value: number, currency: string): string {
  const symbol = currency === 'EUR' ? '€' : currency;
  return `${compactFormatter.format(value)} ${symbol}`;
}
